import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CyberTopologyShare, CyberTopologySummary } from '../../../models/cyber-topology.model';
import { KeycloakUser } from '../../../models/keycloak-user.model';
import { Organization } from '../../../models/organization.model';
import { CyberTopologyService } from '../../../services/cyber-topology.service';
import { KeycloakUserService } from '../../../services/keycloak-user.service';
import { OrganizationService } from '../../../services/organization.service';

/**
 * Grants read access to a topology.
 *
 * Sharing conveys viewing and nothing else, so there is no permission to pick — only who.
 * The owner is excluded from the user list, since they already have every right there is.
 */
@Component({
  selector: 'app-topology-share-dialog',
  standalone: true,
  imports: [],
  templateUrl: './topology-share-dialog.html',
  styleUrl: './topology-share-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopologyShareDialogComponent implements OnInit {
  private topologyService = inject(CyberTopologyService);
  private userService = inject(KeycloakUserService);
  private organizationService = inject(OrganizationService);

  topology = input.required<CyberTopologySummary>();
  closed = output<void>();

  shares = signal<CyberTopologyShare[]>([]);
  users = signal<KeycloakUser[]>([]);
  organizations = signal<Organization[]>([]);
  loading = signal(true);
  busy = signal(false);
  error = signal<string | null>(null);

  selectedUserId = signal('');
  selectedOrganizationId = signal('');

  private sharedSubjects = computed(
    () => new Set(this.shares().map((share) => `${share.subjectType}:${share.subjectId}`)),
  );

  /** Only subjects that do not already have access — re-sharing is a no-op, so hide it. */
  availableUsers = computed(() =>
    this.users().filter(
      (user) =>
        user.id !== this.topology().ownerUserId && !this.sharedSubjects().has(`USER:${user.id}`),
    ),
  );

  availableOrganizations = computed(() =>
    this.organizations().filter(
      (organization) => !this.sharedSubjects().has(`ORGANIZATION:${organization.id}`),
    ),
  );

  /**
   * A required input is not populated until after the component is constructed, so none of
   * this may run in the constructor: reading `topology()` there throws NG0950 and the dialog
   * never renders at all.
   */
  ngOnInit(): void {
    this.reload();

    this.userService.getAll().subscribe({
      next: (users) => this.users.set(users),
      error: () => this.error.set('Could not load the user list.'),
    });
    this.organizationService.getAll().subscribe({
      next: (organizations) => this.organizations.set(organizations),
      error: () => this.error.set('Could not load the organization list.'),
    });
  }

  private reload(): void {
    this.loading.set(true);
    this.topologyService.listShares(this.topology().id).subscribe({
      next: (shares) => {
        this.shares.set(shares);
        this.loading.set(false);
      },
      error: (err: { error?: { message?: string } }) => {
        this.error.set(err?.error?.message ?? 'Could not load the current shares.');
        this.loading.set(false);
      },
    });
  }

  onUserChange(event: Event): void {
    this.selectedUserId.set((event.target as HTMLSelectElement).value);
  }

  onOrganizationChange(event: Event): void {
    this.selectedOrganizationId.set((event.target as HTMLSelectElement).value);
  }

  shareWithUser(): void {
    const userId = this.selectedUserId();
    if (!userId) return;
    this.add('USER', userId, () => this.selectedUserId.set(''));
  }

  shareWithOrganization(): void {
    const organizationId = this.selectedOrganizationId();
    if (!organizationId) return;
    this.add('ORGANIZATION', organizationId, () => this.selectedOrganizationId.set(''));
  }

  private add(
    subjectType: 'USER' | 'ORGANIZATION',
    subjectId: string,
    reset: () => void,
  ): void {
    this.busy.set(true);
    this.error.set(null);
    this.topologyService.addShare(this.topology().id, subjectType, subjectId).subscribe({
      next: (share) => {
        this.shares.update((shares) =>
          shares.some((existing) => existing.id === share.id) ? shares : [...shares, share],
        );
        reset();
        this.busy.set(false);
      },
      error: (err: { error?: { message?: string } }) => {
        this.error.set(err?.error?.message ?? 'Could not share the topology.');
        this.busy.set(false);
      },
    });
  }

  revoke(share: CyberTopologyShare): void {
    this.busy.set(true);
    this.topologyService.removeShare(this.topology().id, share.id).subscribe({
      next: () => {
        this.shares.update((shares) => shares.filter((existing) => existing.id !== share.id));
        this.busy.set(false);
      },
      error: (err: { error?: { message?: string } }) => {
        this.error.set(err?.error?.message ?? 'Could not revoke the share.');
        this.busy.set(false);
      },
    });
  }

  close(): void {
    this.closed.emit();
  }

  displayName(user: KeycloakUser): string {
    const full = [user.firstName, user.lastName].filter(Boolean).join(' ');
    return full ? `${user.username} (${full})` : user.username;
  }
}
